import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { MOCK_DATA } from '../../../lib/mock/viag/learn/data/mockData';

// Utility function for background colors (same as in LearnDashboard)
const getBackgroundColor = (id) => {
  const colorMap = {
    'AGS001': 'bg-blue-500',
    'AGS002': 'bg-red-500',
    'AGS003': 'bg-indigo-500',
    'AGS004': 'bg-orange-500',
    'AGS005': 'bg-yellow-500',
    'AGS006': 'bg-purple-500',
    'AGS007': 'bg-green-500',
    'AGS008': 'bg-gray-500',
    'AGS009': 'bg-teal-500',
    'AGS010': 'bg-pink-500',
  };
  
  return colorMap[id] || 'bg-gray-400';
};

const MyCoursesPage = () => {
  const { t, locale } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'in_progress', 'not_started', 'completed'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Simulate API call to get courses
    const fetchCourses = async () => {
      setTimeout(() => {
        setCourses(MOCK_DATA.courses);
        setLoading(false);
      }, 800);
    };

    fetchCourses();
  }, []);

  // Filter courses based on status and search term
  const filteredCourses = courses.filter(course => {
    const matchesFilter = 
      filter === 'all' || 
      course.status === filter;
    
    const matchesSearch = 
      searchTerm === '' ||
      (locale === 'vi' ? 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) :
        course.titleEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
      course.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // Get course status in the correct language
  const getStatusLabel = (status) => {
    switch(status) {
      case 'in_progress':
        return t('learn.courseStatus.inProgress');
      case 'completed':
        return t('learn.courseStatus.completed');
      case 'not_started':
        return t('learn.courseStatus.notStarted');
      case 'overdue':
        return t('learn.courseStatus.overdue');
      default:
        return status;
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('learn.myCourses.title')}
        </h1>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t('learn.myCourses.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Filter dropdown */}
          <div className="relative w-full sm:w-auto">
            <select
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">{t('learn.myCourses.allCourses')}</option>
              <option value="in_progress">{t('learn.courseStatus.inProgress')}</option>
              <option value="not_started">{t('learn.courseStatus.notStarted')}</option>
              <option value="completed">{t('learn.courseStatus.completed')}</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Courses grid */}
      {filteredCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map(course => (
            <div key={course.id} className="bg-white rounded-lg shadow overflow-hidden flex flex-col border border-gray-100">
              <div className={`h-36 relative flex items-center justify-center ${getBackgroundColor(course.id)} text-white`}>
                <span className="text-lg font-bold">{locale === 'vi' ? course.title.split(' ').slice(0, 2).join(' ') : course.titleEn.split(' ').slice(0, 2).join(' ')}</span>
                {course.isRequired && (
                  <div className="absolute top-2 right-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                    {t('learn.courseInfo.required')}
                  </div>
                )}
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium bg-white/20">
                  {course.id}
                </div>
              </div>
              <div className="p-4 flex-grow">
                <div className="flex justify-between items-start">
                  <h3 className="font-medium text-gray-900 mb-1 line-clamp-2 flex-1">
                    {locale === 'vi' ? course.title : course.titleEn}
                  </h3>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(course.status)}`}>
                    {getStatusLabel(course.status)}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mt-2 mb-3">
                  {course.instructor} • {course.duration}
                </p>
                
                {course.progress > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">{t('learn.courseInfo.progress')}</span>
                      <span className="text-gray-900 font-medium">{course.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${course.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  {course.dueDate && (
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      <span>{t('learn.courseInfo.due')}: {course.dueDate}</span>
                    </div>
                  )}
                  {course.certification && (
                    <div className="flex items-center">
                      <AcademicCapIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      <span>{t('learn.courseInfo.hasCertification')}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 border-t">
                <Link 
                  to={`/learn/course/${course.id}`}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {course.status === 'completed' 
                    ? t('learn.courseActions.review')
                    : course.progress > 0 
                      ? t('learn.courseActions.continueLearning')
                      : t('learn.courseActions.startLearning')
                  }
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('learn.myCourses.noCourses')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('learn.myCourses.noCoursesDescription')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MyCoursesPage; 